'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

const GOLD_ACCENT = '#faaf40';

function CheckIcon({ color }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" style={{ color: color || GOLD_ACCENT }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function MembershipCard({ plan, position }) {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleSelectPlan = () => {
    setIsLoading(true);
    setTimeout(() => {
      router.push('/join');
    }, 600);
  };

  const isFront = position === 'front';

  return (
    <div
      className={`absolute transition-all duration-700 ease-out transform w-[290px] md:w-[340px] ${
        isFront ? 'z-40 scale-100 opacity-100' : 'z-30 scale-90 opacity-40 blur-[2px]'
      } ${position === 'left' ? '-translate-x-28 md:-translate-x-60' : position === 'right' ? 'translate-x-28 md:translate-x-60' : ''}`}
    >
      <div
        className="rounded-[2.5rem] relative z-10 p-7 md:p-10 h-[670px] flex flex-col transition-all duration-500 overflow-hidden"
        style={{
          // 🌑 Solid Black Border for the front card
          border: isFront ? `5px solid #000000` : `1px solid rgba(250, 175, 64, 0.2)`,
          background: isFront 
            ? `linear-gradient(180deg, ${GOLD_ACCENT} 0%, #c98528 100%)` 
            : 'linear-gradient(160deg, #0d0d0d 0%, #000000 100%)',
          boxShadow: isFront ? '0 45px 100px -20px rgba(0, 0, 0, 0.9)' : 'none'
        }}
      >
        <div className="relative flex flex-col items-center text-center h-full" style={{ color: isFront ? 'black' : 'white' }}>
          
          <div className="mb-2">
            <h2 className="text-3xl font-black uppercase tracking-tighter leading-none mb-1">
              {plan.title}
            </h2>
            <p className="text-[10px] uppercase font-black tracking-[0.4em] opacity-70">{plan.subtitle}</p>
          </div>

          <div className="mt-3 mb-4">
            <span className={`text-[9px] font-black px-4 py-1.5 rounded-full uppercase italic tracking-widest shadow-sm ${isFront ? 'bg-black text-white' : 'bg-red-600 text-white'}`}>
              Limited Time Offer
            </span>
          </div>

          <div className="w-12 h-[3px] bg-current opacity-20 my-2"></div>

          <div className="mb-4">
            <span className="text-xl line-through opacity-30 mr-2 font-bold">${plan.oldPrice}</span>
            <div className="text-7xl font-black inline-block tracking-tighter">
              <span className="text-2xl align-top opacity-70 mr-0.5">$</span>{plan.price}
            </div>
          </div>

          <div className="mb-6 italic text-[13px] font-black leading-snug px-2">
             "{plan.promise}"
          </div>

          <ul className="mb-8 space-y-4 text-left w-full px-1 flex-grow">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-[13px] leading-tight font-black uppercase tracking-tight">
                <CheckIcon color={isFront ? 'black' : GOLD_ACCENT} />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {/* 🔘 ULTRA-ANIMATED BUBBLY BUTTON */}
          <button
            onClick={handleSelectPlan}
            disabled={isLoading}
            className={`
              group relative w-full px-6 py-5 
              rounded-[1.5rem] overflow-hidden 
              font-black uppercase tracking-[0.2em] text-[12px]
              transition-all duration-500 ease-out
              ${isFront 
                ? 'bg-black text-white shadow-2xl hover:scale-105' 
                : 'border-2 border-[#faaf40] text-[#faaf40] hover:bg-[#faaf40] hover:text-black'}
              active:scale-95 disabled:opacity-50
            `}
          >
            {/* 🌊 Liquid Fill Animation Layer */}
            <span 
              className={`
                absolute inset-0 w-full h-full transition-transform duration-500 ease-in-out translate-y-full group-hover:translate-y-0
                ${isFront ? 'bg-[#333333]' : 'bg-[#faaf40]'}
              `}
            />

            {/* ⚡ Shimmer/Sparkle Effect */}
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]" />
            
            <span className={`relative z-10 flex items-center justify-center gap-3 transition-colors duration-300 ${!isFront && 'group-hover:text-black'}`}>
              {isLoading ? 'Processing...' : 'Secure My Spot'}
              {!isLoading && (
                <div className="relative w-5 h-5 flex items-center justify-center">
                  <svg className="w-4 h-4 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </div>
              )}
            </span>
          </button>

        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}